// BM-H398 - s2_family. Children live in the register; `children[]` holds
// person references the engine resolves for C-08 (minor by date of birth at
// submission).

import { QuestionRow, SectionIntro, Select, SmallButton, YesNo, sectionProps } from "./fields";
import { PersonEditor, PeopleList } from "./PersonPicker";
import { findPerson, newPerson } from "./willsPayload";

const CHILD_TYPE_OPTIONS = [
  { value: "biological", label: "My biological child" },
  { value: "adopted", label: "Adopted" },
  { value: "step", label: "Stepchild" },
  { value: "foster", label: "Foster child" },
];

export default function S2Family({ section, setSection, payload, update, person }) {
  const { s, set, nk, setNk } = sectionProps(section, setSection);
  const children = s.children || [];

  function addChild() {
    const child = newPerson({ relationship: "child" });
    update((d) => { d.couple.people_register.push(child); });
    set({ has_children: true, children: [...children, { person_id: child.id, child_type: null }] });
  }

  function editChild(i, patch) {
    update((d) => {
      const p = d.couple.people_register.find((x) => x.id === children[i].person_id);
      if (p) Object.assign(p, patch);
    });
  }

  function removeChild(i) {
    const pid = children[i].person_id;
    update((d) => { d.couple.people_register = d.couple.people_register.filter((x) => x.id !== pid); });
    set({ children: children.filter((_, j) => j !== i) });
  }

  return (
    <div data-testid="section-s2_family">
      <SectionIntro title="Your family">
        This is where we build a picture of your family, so that when you name someone later we know exactly who you mean.
      </SectionIntro>

      <QuestionRow qid="s2_family.has_children" label="Do you have children?">
        <YesNo testid="s2-has-children" value={s.has_children} onChange={(v) => set({ has_children: v, children: v ? children : [] })} />
      </QuestionRow>

      {s.has_children === true && (
        <div data-testid="s2-children">
          {children.map((c, i) => {
            const child = findPerson(payload, c.person_id) || {};
            return (
              <div key={c.person_id} data-testid={`s2-child-${i}`} style={{ border: "1px solid currentColor", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <PersonEditor person={{ ...child, relationship: child.relationship || "child" }} onChange={(p) => editChild(i, p)} testidPrefix={`s2-child-${i}`} showCapacity />
                <QuestionRow qid="s2_family.children.child_type" label="This child is" testid={`s2-child-${i}-q-type`}>
                  <Select testid={`s2-child-${i}-type`} value={c.child_type} options={CHILD_TYPE_OPTIONS} onChange={(v) => set({ children: children.map((x, j) => (j === i ? { ...x, child_type: v } : x)) })} />
                </QuestionRow>
                {child.is_minor_at_submission && <p data-testid={`s2-child-${i}-minor`} style={{ fontSize: 13 }}>Under 18. We will ask you about guardians.</p>}
                <SmallButton testid={`s2-child-${i}-remove`} onClick={() => removeChild(i)}>Remove</SmallButton>
              </div>
            );
          })}
          <SmallButton testid="s2-add-child" onClick={addChild}>Add a child</SmallButton>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <QuestionRow qid="s2_family.deceased_children_with_issue" label="Have any of your children died leaving children of their own?" notKnown={nk("deceased_children_with_issue")} onNotKnown={(on) => setNk("deceased_children_with_issue", on)}>
          <YesNo testid="s2-deceased-children" value={s.deceased_children_with_issue} disabled={nk("deceased_children_with_issue")} onChange={(v) => set({ deceased_children_with_issue: v })} />
        </QuestionRow>
      </div>

      <PeopleList
        payload={payload}
        update={update}
        items={s.financial_dependants || []}
        onChange={(items) => set({ financial_dependants: items })}
        qid="s2_family.financial_dependants"
        label="Anyone who depends on you financially"
        testid="s2-dependants"
        exclude={[person?.id]}
      />
    </div>
  );
}
